import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { each as _each, get as _get, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { BillAccountActions, BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { BillingRegistrationSelectors } from '@amfam/billing/registration/data-access';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util';
import { env } from '@amfam/environment';
import { EsignatureActions, EsignatureSelectors } from '@amfam/policy/e-signature/data-access';
import {
  GoPaperLessActions,
  GoPaperLessSelectors,
  GoPaperlessService
} from '@amfam/policy/go-paperless/data-access';
// Store
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BillAccount, OverviewAnalytics } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService, WindowRef } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import { RegisterBillAccModel } from '../../../billing/shared/billingAccount.model';
import * as fromRoot from '../../../core/store';
import { UBI_PROGRAM } from '../../../policy/know-your-drive-partial-household/models/kyd-program-category.model';

@Component({
  selector: 'programs-overview',
  templateUrl: './programs-overview.component.html',
  styleUrls: ['./programs-overview.component.scss'],
  standalone: false
})
export class ProgramsOverviewComponent implements OnInit, OnDestroy {
  public loading: Observable<boolean>;
  public policyEnrolled: boolean;

  public memberIdentifier: number;
  public kydEligible: boolean;
  public typeOfProgram: string;
  public clickGoPaperlessAnalytics = OverviewAnalytics.clickGoPaperless;
  public multiAutopayOverviewAnalytic = OverviewAnalytics.clickMultiAutopaySetup;
  public showModal = false;
  public showRegistrationModal = false;

  public mmwCardEnrolledTitle = this.copyService.getCopy('overview.mmwCardEnrolledTitle');
  public mmwCardEnrolledMessage = this.copyService.getCopy('overview.mmwCardEnrolledMessage');

  public dmwCardEnrolledTitle = this.copyService.getCopy('overview.dmwCardEnrolledTitle');
  public dmwCardEnrolledMessage = this.copyService.getCopy('overview.dmwCardEnrolledMessage');

  showAutopayCard: Observable<boolean>;
  showPaperlessCard: Observable<boolean>;
  showKydSmartphoneCard: Observable<boolean>;
  showPaperlessDiscountBanner: Observable<boolean>;
  showAutopayDiscountBanner: Observable<boolean>;
  showKydSmartphoneBanner: Observable<boolean>;
  showMMWDMWCard = false;
  showKYDManageEnrollmentDuringMigration$: Observable<boolean>;

  isNotEnrolledKydSmartphone: Observable<boolean>;
  isPartner: Observable<boolean>;

  // observable for handling section loading
  public multipleAutoPayFeature$: Observable<boolean>;
  public billingEligibleToGoPaperless$: Observable<boolean>;
  public isPolicyGoPaperless$: Observable<boolean>;
  public isSignatureRequired: boolean;
  public billAccsRegistered: boolean;
  public hasRegistrationError$: Observable<boolean>;
  eligibleForUtah$: Observable<boolean>;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  public content: any;
  public amfamDotCom: string;
  isKydEnrolled$: Observable<boolean>;

  constructor(
    private store: Store<fromRoot.RootState>,
    private copyService: CopyService,
    private featureFlagService: FeatureFlagService,
    private goPaperlessService: GoPaperlessService,
    private utilService: UtilService,
    public spinner: LoadingSpinnerService,
    public roleService: ImpersonateRolesService,
    private transmuteService: BillAccountTransmuteService,
    private autoPayUtil: AutoPayUtilService,
    private win: WindowRef,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit() {
    this.isPartner = this.store.select(BrandSelectors.selectIsPartner);
    this.billingEligibleToGoPaperless$ = this.store.select(fromRoot.billingEligibleToGoPaperless);
    this.isPolicyGoPaperless$ = this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless);

    this.content = this.copyService.getComponentData('overview');

    this.loading = this.store.select(fromRoot.programsLoading);

    this.showAutopayCard = this.store.select(fromRoot.isAutopayEligible);
    this.showPaperlessCard = this.store.select(fromRoot.isEligibleToGoPaperless);
    this.eligibleForUtah$ = this.store.select(userQuery.selectUserStateCode).pipe(
      map(state => {
        const states = this.copyService.getCopy('kydSmartphonePhh.kydmodal.states');
        const effectiveDate = this.copyService.getCopy('kydSmartphonePhh.kydmodal.date');
        const isValidDate = new Date() >= new Date(effectiveDate);
        return states.includes(state) && isValidDate;
        return states.includes(state);
      })
    );
    this.isNotEnrolledKydSmartphone = this.store.select(fromRoot.showKydSmartphoneBanner);
    this.showKydSmartphoneCard = combineLatest([
      this.store.select(fromRoot.isKydSmartphoneEligible),
      this.isNotEnrolledKydSmartphone,
      this.eligibleForUtah$
    ]).pipe(
      map(
        ([kydEligible, notEnrolled, eligibleForUtah]) =>
          kydEligible && !(notEnrolled && eligibleForUtah)
      )
    );

    this.isKydEnrolled$ = this.store.select(fromRoot.getKydSmartphoneEnrollments).pipe(
      takeUntil(this.stop$),
      map(summaries =>
        summaries.some(summary => {
          return (
            summary.enrollmentStatus === 'Enrolled' || summary.enrollmentStatus === 'Waiting Period'
          );
        })
      )
    );
    this.showKYDManageEnrollmentDuringMigration$ = combineLatest([
      this.store.select(fromRoot.isWithinKydMigrationPeriod),
      this.isKydEnrolled$
    ]).pipe(
      map(([withinMigrationPeriod, isKydEnrolled]) => withinMigrationPeriod && isKydEnrolled)
    );
    this.showAutopayDiscountBanner = this.store
      .select(fromRoot.showAutopayBanner)
      .pipe(map(hasQualifyingPolicy => hasQualifyingPolicy));

    this.showPaperlessDiscountBanner = this.store
      .select(fromRoot.showPaperlessBanner)
      .pipe(map(hasQualifyingPolicy => hasQualifyingPolicy));

    this.showKydSmartphoneBanner = this.store
      .select(fromRoot.showKydSmartphoneBanner)
      .pipe(map(hasQualifyingPolicy => hasQualifyingPolicy));

    this.initPrograms();

    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingRegistration),
      this.store.select(BillingRegistrationSelectors.selectPendingRegistrationError),
      (isUpdating, registrationError) => ({ isUpdating, registrationError })
    ).subscribe(state => {
      if (!state.isUpdating && state.registrationError) {
        this.spinner.stop();
        this.showRegistrationModal = true;
      } else if (state.isUpdating) {
        this.spinner.start();
      }
    });

    /*
     * Check eligibility for current user for MMW Card.
     * policyEligibilities have a reasonCode.
     * If reasonCode is 400007 then current user will be eligible for MMW & DMW.
     */
    this.store
      .select(fromRoot.getKydPolicyEligibility)
      .pipe(takeUntil(this.stop$))
      .subscribe(PolicyEligibility => {
        PolicyEligibility.forEach(policyEligibility => {
          if (policyEligibility.reasonCode === '400007') {
            this.showMMWDMWCard = true;
          }
        });
      });
  }

  openModal() {
    this.showModal = true;
  }

  goPaperLess(): void {
    this.spinner.start({
      blockActions: true
    });

    const correlationId = this.utilService.generateId();

    // Get the list of unregistered bill accounts and dispatch an action
    // to register all of them.
    let unregisteredBillAccounts: BillAccount[];

    this.store
      .select(BillAccountsSelectors.selectUnregisteredBillAccounts)
      .pipe(takeUntil(this.stop$))
      .subscribe(billAccs => {
        unregisteredBillAccounts = billAccs;
      });

    this.registerBillAccounts(unregisteredBillAccounts, correlationId);

    // Check if we need a signature (GA), else go ahead with the policy
    // enrollment.
    if (!this.isSignatureRequired && !this.policyEnrolled) {
      this.store.dispatch(GoPaperLessActions.EnrollGoPaperless());
      this.store
        .select(GoPaperLessSelectors.selectGoPaperlessError)
        .pipe(takeUntil(this.stop$))
        .subscribe(hasError => {
          if (hasError) {
            this.spinner.stop();
          }
        });
    }

    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(BillAccountsSelectors.selectUnregisteredBillAccounts),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(GoPaperLessSelectors.selectGoPaperlessLoading),
      this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless),
      (
        billAccounts,
        unRegisteredBillAccs,
        billAccountsLoading,
        goPaperlessLoading,
        isEnrolled
      ) => ({
        billAccounts: billAccounts,
        unRegisteredBillAccs: unRegisteredBillAccs,
        loading: billAccountsLoading || goPaperlessLoading,
        isEnrolled: isEnrolled
      })
    )
      .pipe(
        filter(state => !state.loading),
        withLatestFrom(
          this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities)
        ),
        map(([state, matchedBillAccountNotificationState]) => {
          // Filter only those bill accounts which have the correct co-relation id's
          // associated with them
          const filtered = state.billAccounts.filter(
            ba =>
              matchedBillAccountNotificationState[ba.billAccountNumber].correlationId ===
              correlationId
          );
          return Object.assign({}, state, { billAccounts: filtered });
        }),
        filter(
          state =>
            state.billAccounts.length === state.unRegisteredBillAccs.length &&
            this.matchedBillAccWithUnregisteredAccs(state.billAccounts, state.unRegisteredBillAccs)
        ),
        takeUntil(this.stop$),
        withLatestFrom(
          this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities)
        )
      )
      .subscribe(
        ([state, notificationsEntities]) => {
          this.spinner.stop();
          let unRegisteredBillAccs: BillAccount[] = [];
          // Get the list of bill accounts which failed during the registration.
          _each(state.billAccounts, billAcc => {
            if (notificationsEntities[billAcc.billAccountNumber].updateRegistrationError) {
              unRegisteredBillAccs = [...unRegisteredBillAccs, billAcc];
            }
          });
          if ((unRegisteredBillAccs.length > 0 || !state.isEnrolled) && !this.isSignatureRequired) {
            this.showRegistrationModal = true;
          } else {
            if (this.isSignatureRequired && !this.policyEnrolled) {
              // If Signature is required we need to make sure we have all the billacounts registered
              // before we direct to docusign.
              if (unRegisteredBillAccs.length > 0) {
                // We need to show the modal with the pending billaccounts to be registered.
                this.showRegistrationModal = true;
              } else {
                // We need signature to enroll the policy documents, get the signature by taking
                // them to the docusign.

                this.store
                  .pipe(select(BrandSelectors.selectExperienceId), take(1))
                  .subscribe(experienceId => {
                    const callbackUrl = this.utilService.getAbsoluteUrl(
                      'policies/gopaperless/signature',
                      experienceId
                    );
                    const signatureRequest =
                      this.goPaperlessService.createSignatureRequest(callbackUrl);
                    this.store.dispatch(EsignatureActions.SignESignature(signatureRequest));
                  });

                this.spinner.start({
                  blockActions: true
                });
              }
            } else {
              // We have registered all the bill accounts let go to the confirmation screen.
              this.store.dispatch(
                fromRouterActions.Go({
                  path: ['/policies/gopaperless/confirmation']
                })
              );
            }
          }
        },
        () => {
          this.spinner.stop();
          this.showRegistrationModal = true;
        }
      );
  }

  public navigateToAutopaySetupPage() {
    this.autoPayUtil.routeToMultipleAutoPaySelection('overview');
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }

  private initPrograms() {
    this.loadKydProgram();

    // Add dataLoading and the getKydEligibilityLoaded select below here
    combineLatest(
      this.store.select(BillAccountsSelectors.selectUnregisteredBillAccounts),
      this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless),
      this.store.select(EsignatureSelectors.selectESignatureRequired),
      (unRegisteredBillAccs, eDelivery, eSignatureRequired) => ({
        unRegisteredBillAccs: unRegisteredBillAccs,
        isEnrolled: eDelivery,
        eSignatureRequired: eSignatureRequired
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.policyEnrolled = state.isEnrolled;
        this.billAccsRegistered = !_get(state, 'unRegisteredBillAccs.length', '');
        this.isSignatureRequired = state.eSignatureRequired;
      });
  }

  private loadKydProgram() {
    let kydChecked = false;

    this.kydEligible = false;

    this.store
      .select(fromRoot.getKydPolicyEligibility)
      .pipe(takeUntil(this.stop$))
      .subscribe(policyEligibilities => {
        policyEligibilities.forEach(policyEligibility => {
          if (policyEligibility.isPolicyEligible && policyEligibility.isStateEligible) {
            switch (policyEligibility.programCategory) {
              case UBI_PROGRAM.AUTOMATIC:
                if (!kydChecked) {
                  this.canSeeKydCard();
                }
                kydChecked = true;
                break;
            }
          }
        });
      });
  }

  private canSeeKydCard() {
    this.kydEligible = false;

    combineLatest(
      this.store.select(fromRoot.getKydEligibleRisks),
      this.store.select(fromRoot.getKydVehicleEnrollmentSummaries),
      (risks, vehicleEnrollmentSummaries) =>
        risks.length > 0 &&
        vehicleEnrollmentSummaries.some(vehicleEnrollment => {
          const summary = vehicleEnrollment.enrollmentSummary || {};

          return (
            summary.enrollmentStatus !== 'Enrolled' && summary.enrollmentStatus !== 'Waiting Period'
          );
        })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(eligible => {
        this.kydEligible = eligible;
      });
  }

  private buildRegisterationPayload(
    billAcc: BillAccount,
    correlationId: string
  ): RegisterBillAccModel {
    const billAccNumber: string = _get(billAcc, 'billAccountNumber', '');
    const payload: RegisterBillAccModel = {
      billAccountNumber: billAcc.billAccountNumber,
      correlationId: correlationId,
      isUnregister: false,
      registrationData: {
        policyNumber: _get(billAcc, 'policyList[0].policyNumber'),
        accountName:
          'Billing Account End ' +
          billAccNumber.substring(billAccNumber.length - 4, billAccNumber.length),
        reminder: '7',
        associated: billAcc.associated ? true : false
      }
    };
    return payload;
  }

  private registerBillAccounts(billAccounts: BillAccount[], correlationId: string) {
    _each(billAccounts, billAcc => {
      const payload: RegisterBillAccModel = this.buildRegisterationPayload(billAcc, correlationId);
      this.store.dispatch(BillAccountActions.billAccountUpdateRegistrationSubmit(payload));
    });
  }

  private matchedBillAccWithUnregisteredAccs(
    billAccs: BillAccount[],
    unRegisteredBillAccs: BillAccount[]
  ): boolean {
    let matched = true;
    billAccs.forEach(billAcc => {
      if (!_some(unRegisteredBillAccs, { billAccountNumber: billAcc.billAccountNumber })) {
        matched = false;
        return;
      }
    });
    return matched;
  }

  learnMoreBtn(program) {
    this.amfamDotCom = env.links.amfamDotComUrl;
    if (program === 'dmw') {
      this.win.nativeWindow.open(this.amfamDotCom + 'insurance/car/drivemyway', '_blank');

      // click analytic for Card DMW: Learn More Button
      this.analyticsFacade.trackButtonClick(OverviewAnalytics.clickCardDMWLearnMore);
    } else if (program === 'mmw') {
      this.win.nativeWindow.open(this.amfamDotCom + 'insurance/car/milesmyway', '_blank');

      // click analytic for Card MMW: Learn More Button
      this.analyticsFacade.trackButtonClick(OverviewAnalytics.clickCardMMWLearnMore);
    }
  }
}
